"use strict";

exports.__esModule = true;
exports.default = exports.ReactSelect = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _clsx = _interopRequireDefault(require("clsx"));

var _reactSelect = _interopRequireDefault(require("react-select"));

var _Select = require("./Select.styles");

var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));

var _useToggler2 = _interopRequireDefault(require("../../hooks/useToggler"));

var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));

var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));

var _Tooltip = _interopRequireDefault(require("../Tooltip/Tooltip"));

var _excluded = ["ref"],
    _excluded2 = ["id", "name", "options", "value", "label", "placeholder", "helper", "floatLabel", "multiple", "searchable", "clearable", "disabled", "required", "onChange", "onBlur", "onFocus", "onError", "className", "withErrorTooltip"];

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var selectOptionShape = _propTypes.default.oneOfType([_propTypes.default.shape({
  value: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]).isRequired,
  label: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]).isRequired
}), _propTypes.default.shape({})]);

var ClearIndicator = function ClearIndicator(_ref) {
  var _ref$innerProps = _ref.innerProps,
      ref = _ref$innerProps.ref,
      restInnerProps = _objectWithoutPropertiesLoose(_ref$innerProps, _excluded);

  return /*#__PURE__*/_react.default.createElement("button", _extends({}, restInnerProps, {
    ref: ref,
    className: "uk-select__clearButton"
  }), "\xD7");
};
/**
 * The Select component allows a user to select one (or multiple) value(s) from a list of options.
 *
 * This component is an opinionated wrapper around <a href="https://react-select.com">React Select</a>.
 */


var ReactSelect = function ReactSelect(props) {
  var controlledId = props.id,
      name = props.name,
      _props$options = props.options,
      options = _props$options === void 0 ? [] : _props$options,
      controlledValue = props.value,
      initialLabel = props.label,
      placeholder = props.placeholder,
      helper = props.helper,
      floatLabel = props.floatLabel,
      multiple = props.multiple,
      searchable = props.searchable,
      clearable = props.clearable,
      disabled = props.disabled,
      required = props.required,
      onChange = props.onChange,
      onBlur = props.onBlur,
      onFocus = props.onFocus,
      onError = props.onError,
      className = props.className,
      withErrorTooltip = props.withErrorTooltip,
      attributes = _objectWithoutPropertiesLoose(props, _excluded2);

  var selectRef = _react.default.useRef(null);

  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;

  var _useToggler = (0, _useToggler2.default)(false),
      isOpen = _useToggler.isOpen,
      setOpen = _useToggler.setOpen,
      setClose = _useToggler.setClose;

  var _React$useState = _react.default.useState(controlledValue),
      selectValue = _React$useState[0],
      setSelectValue = _React$useState[1];

  var _React$useState2 = _react.default.useState(''),
      selectedOption = _React$useState2[0],
      setSelectedOption = _React$useState2[1];

  _react.default.useEffect(function () {
    return setSelectValue(controlledValue || (multiple ? [] : ''));
  }, [controlledValue, multiple]);

  var _useFormElementValida = (0, _useFormElementValidation.default)(selectValue, {
    required: required
  }),
      errors = _useFormElementValida[0],
      methods = _useFormElementValida[1];

  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
    id: id,
    value: selectedOption
  })),
      label = _useLabel.label;

  _react.default.useEffect(function () {
    if (multiple) {
      var valuesArray = [];

      if (Array.isArray(selectValue)) {
        selectValue.map(function (item) {
          var selectedValue = options.filter(function (o) {
            return [item].includes(o.value);
          });
          valuesArray.push(selectedValue[0]);
          return null;
        });
      }

      setSelectedOption(valuesArray);
    } else {
      setSelectedOption(options.find(function (o) {
        return o.value === selectValue;
      }) || '');
    }
  }, [selectValue, options, multiple]);

  _react.default.useEffect(function () {
    if (typeof onError === 'function') {
      onError({
        name: selectRef.current.props.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);

  var handleChange = function handleChange(newOptions) {
    setSelectedOption(newOptions);

    if (multiple) {
      var newValuesArray = [];

      if (Array.isArray(newOptions)) {
        newValuesArray = newOptions.map(function (item) {
          return item.value;
        });
      }

      methods.onSelect(newValuesArray);

      if (typeof onChange === 'function') {
        onChange(newValuesArray, newOptions);
      }
    } else {
      var newOptionValue = newOptions && newOptions.value || '';
      methods.onSelect(newOptionValue);

      if (typeof onChange === 'function') {
        onChange(newOptionValue, selectRef.current.props.name, newOptions);
      }
    }
  };

  var handleBlur = function handleBlur(event) {
    methods.onSelect(selectValue);

    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };

  var containerClasses = (0, _clsx.default)('uk-form-item', 'uk-select', multiple && 'uk-select--multiple', helper.length > 0 && 'has-helper', errors.length > 0 && 'has-error', errors.length > 0 && withErrorTooltip && 'has-error--tooltip', (errors.length > 0 || disabled) && 'is-frozen', isOpen && 'open', searchable && 'searchable', disabled && 'disabled', clearable && selectValue && 'is-clearable', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container"
  }, /*#__PURE__*/_react.default.createElement(_reactSelect.default, _extends({
    ref: selectRef,
    components: {
      ClearIndicator: ClearIndicator
    },
    styles: _Select.customStyles,
    id: id,
    name: name || id,
    instanceId: id,
    className: "uk-select-container",
    classNamePrefix: "uk-select",
    value: selectedOption || '',
    options: options,
    placeholder: placeholder && !floatLabel ? placeholder : null,
    onChange: handleChange,
    onBlur: handleBlur,
    isSearchable: searchable,
    isClearable: clearable,
    isDisabled: disabled || !options.length,
    onMenuOpen: setOpen,
    onMenuClose: setClose,
    menuPlacement: "auto",
    required: required,
    isMulti: multiple,
    "aria-describedby": (name || id) + "-helper-text",
    "aria-errormessage": (name || id) + "-errors-text",
    "aria-invalid": errors.length > 0
  }, attributes)), errors.length > 0 && withErrorTooltip && /*#__PURE__*/_react.default.createElement(_Tooltip.default, {
    trigger: "!",
    className: "uk-form-item__error-tooltip"
  }, /*#__PURE__*/_react.default.createElement("div", {
    id: (name || id) + "-errors-text"
  }, errors))), errors.length > 0 && !withErrorTooltip && /*#__PURE__*/_react.default.createElement("span", {
    className: "uk-form-item__error",
    id: (name || id) + "-errors-text"
  }, errors), errors.length > 0 && !withErrorTooltip ? '' : helper.length > 0 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, helper));
};

exports.ReactSelect = ReactSelect;
ReactSelect.propTypes = {
  /**
   * ID of the Select (used to bind the label to the Select)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,

  /**
   * Name of the Select (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,

  /**
   * Array of options used by the Select
   */
  options: _propTypes.default.arrayOf(selectOptionShape),

  /**
   * Default value of the Select
   */
  value: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string, _propTypes.default.array]),

  /**
   * Label of the Select
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),

  /**
   * Select placeholder
   */
  placeholder: _propTypes.default.string,

  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,

  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,

  /**
   * Enable multiple selected options
   */
  multiple: _propTypes.default.bool,

  /**
   * Add an input within the Select to filter the options
   */
  searchable: _propTypes.default.bool,

  /**
   * Add a button to clear the selected option
   */
  clearable: _propTypes.default.bool,

  /**
   * Set the Select as disabled
   */
  disabled: _propTypes.default.bool,

  /**
   * Form-validation: set the Select as required
   */
  required: _propTypes.default.bool,

  /**
   * Form-validation (if multiple): minimum number of selected options
   */
  atLeast: _propTypes.default.number,

  /**
   * Form-validation (if multiple): maximum number of selected options
   */
  atMost: _propTypes.default.number,

  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,

  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,

  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,

  /**
   * Display error in tooltip otherwise display error below input.
   */
  withErrorTooltip: _propTypes.default.bool
};
ReactSelect.defaultProps = {
  options: [],
  placeholder: '',
  helper: '',
  floatLabel: false,
  multiple: false,
  searchable: false,
  clearable: false,
  disabled: false,
  required: false,
  withErrorTooltip: true
};
ReactSelect.displayName = 'Select';
var _default = ReactSelect;
exports.default = _default;